import { useMemo } from "react";

/**
 * MomentumRing — radial gauge showing overall Creator Momentum (0-100).
 *
 * Design intent (Espresso & Brass theme):
 * - Warm brass gradient, subtle grain
 * - Overall number is large, label soft & motivational
 * - The score is FEEDBACK — never the hero. The 3 moves are the hero.
 */
export default function MomentumRing({ overall = 0, label = "", emoji = "", size = 116, stroke = 8 }) {
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, overall));
  const dash = useMemo(() => (clamped / 100) * circ, [clamped, circ]);
  const gradientId = "momentum-ring-gradient";

  return (
    <div
      data-testid="momentum-ring"
      className="relative inline-flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="var(--seal)" stopOpacity="0.95" />
            <stop offset="100%" stopColor="var(--seal)" stopOpacity="0.55" />
          </linearGradient>
        </defs>
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--ink-22)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circ - dash}`}
          style={{ transition: "stroke-dasharray 900ms cubic-bezier(0.22, 1, 0.36, 1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="font-heading text-3xl leading-none" style={{ color: "var(--ink)", fontVariationSettings: "'wght' 500" }}>
          {clamped}
        </div>
        <div className="text-[9px] uppercase tracking-[0.22em] mt-1" style={{ color: "var(--ink-55)" }}>
          {emoji} {label ? label.split(" ")[0] : "MOMENTUM"}
        </div>
      </div>
    </div>
  );
}
